import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaHome,
  FaClipboardList,
  FaChartBar,
  FaBoxOpen,
  FaUsers,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBan,
  FaClock,
  FaWarehouse,
  FaTruck,
  FaHourglassHalf,
  FaRupeeSign,
  FaBullhorn,
  FaFileInvoice,
  FaUserTie,
  FaFileContract,
  FaHistory,
  FaDatabase,
  FaBook,
  FaShieldAlt,
  FaBolt,
  FaWindowClose,
  FaCogs,
} from "react-icons/fa";
import "./Sidebar.css";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

function Sidebar() {
  const [collapsed, setCollapsed] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [role, setRole] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setRole(snap.data().role);
    });

    const loadCompanyInfo = async () => {
      try {
        const cSnap = await getDoc(doc(db, "companyInfo", "default"));
        if (cSnap.exists()) {
          setCompanyName(cSnap.data().name || "");
        }
      } catch (err) {
        console.error("Failed to load company info for sidebar:", err);
      }
    };
    loadCompanyInfo();

    return () => unsub();
  }, []);

  const normalizedRole = role?.toLowerCase().trim().replace(/\s/g, "") || "";
  console.log("Sidebar Role Check:", normalizedRole);

  const menuItems = [
    { divider: true, label: "SALES" },
    { title: "Sales Control Center", icon: <FaChartBar style={{ color: "#4f46e5" }} />, path: "/operator" },
    { title: "System Configuration", icon: <FaCogs style={{ color: "#718096" }} />, path: "/settings" },
    { title: "HW CDM & Niche Sales", icon: <FaRupeeSign style={{ color: "#48bb78" }} />, path: "/hawker-cdm" },

    ...( ["admin", "superadmin"].includes(normalizedRole)
      ? [
          { divider: true, label: "ORDERS" },
          {
            title: "Orders",
            icon: <FaFileInvoice style={{ color: "#f56565" }} />,
            path: "/salesman-order",
            children: [
              { title: "Salesman Order", path: "/salesman-order" },
              { title: "Wholesale Order", path: "/wholesale-order" },
              { title: "SWD", path: "/swd" },
            ],
          },
        ]
      : []),

    { divider: true, label: "REPORTS" },
    {
      title: "Billing Reports",
      icon: <FaFileContract style={{ color: "#667eea" }} />,
      path: "/reports",
      children: [
        { title: "Order Summary", path: "/reports/order-head" },
        { title: "Outlet Invoices", path: "/reports/outlet-invoices" },
        ...( ["admin", "superadmin", "operator"].includes(normalizedRole)
          ? [
              { title: "E-Invoice JSON Center", path: "/einvoice" },
              { title: "E-Way Bill Center", path: "/ewaybill" }
            ]
          : []
        ),
      ],
    },

    {
      title: "Daily Reports",
      icon: <FaHistory style={{ color: "#ed64a6" }} />,
      path: "/reports/sales-summary",
      children: [
        { title: "Sales Summary", path: "/reports/sales-summary" },
        { title: "Tally Sheet", path: "/reports/tally-sheet" },
        { title: "Daily Item Report", path: "/reports/new-report" },
        { title: "📤 DMS Upload Report", path: "/reports/dms-upload" },
        { title: "📊 HSN Summary Report", path: "/reports/hsn-summary" },
        { title: "Sales Lines Report", path: "/reports/order-lines" },
        { title: "Outlet Orders Report", path: "/reports/outlet-orders" },
        { title: "CDM Orders Report", path: "/reports/cdm-orders" },
      ],
    },

    ...( ["admin", "superadmin"].includes(normalizedRole)
      ? [
          { divider: true, label: "CASHIER" },
          {
            title: "Cashier",
            icon: <FaUserTie style={{ color: "#38b2ac" }} />,
            path: "/cashier",
            children: [
              { title: "Cashier Entry", path: "/cashier" },
              { title: "Cashier Approval", path: "/cashier-Approval" },
            ],
          },
        ]
      : []),

    { divider: true, label: "MANAGEMENT" },

    {
      title: "Masters",
      icon: <FaDatabase style={{ color: "#4fd1c5" }} />,
      path: "/masters",
      children:
        ["admin", "superadmin", "operator"].includes(normalizedRole)
          ? [
              { title: "SKU Master", path: "/sku-master" },
              { title: "CDM Master", path: "/cdm-master" },
              { title: "Outlet Master", path: "/outlet-master" },
              { title: "Party Manager", path: "/party-manager" },
            ]
          : [
              { title: "Party Manager", path: "/party-manager" },
            ],
    },

    { divider: true, label: "WAREHOUSE" },

    ...( ["admin", "superadmin", "operator"].includes(normalizedRole)
      ? [
          { title: "ITC Purchase", icon: <FaTruck style={{ color: "#f6ad55" }} />, path: "/warehouse-inward" },
          { title: "Closing Stock", icon: <FaWarehouse style={{ color: "#fc8181" }} />, path: "/warehouse-close" },
        ]
      : []),

    ...(normalizedRole === "warehouse"
      ? [{ title: "🏭 Closing Stock", icon: <FaBoxOpen />, path: "/warehouse-close" }]
      : []),

    { divider: true, label: "COMMUNICATIONS" },
    ...( ["admin", "superadmin", "operator"].includes(normalizedRole)
      ? [{ title: "Hawker Communications", icon: <FaBullhorn style={{ color: "#9f7aea" }} />, path: "/dashboard-manager" }]
      : []),

  ];

  const goTo = (p) => navigate(p);

  return (
    <div
      className={`sb-ultra ${collapsed ? "collapsed" : ""}`}
      onMouseEnter={() => setCollapsed(false)}
      onMouseLeave={() => {
        setCollapsed(true);
        setHovered(null);
      }}
    >
      <div className="sb-brand">
        <div className="sb-logo"><span>Q</span></div>
        {!collapsed && (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.1", textAlign: "left" }}>
            <span className="sb-brand-text">QuickBill</span>
            {companyName && (
              <span style={{ fontSize: "11px", color: "#a0aec0", fontWeight: "600", marginTop: "2px" }}>
                {companyName}
              </span>
            )}
          </div>
        )}
      </div>

      <ul className="sb-menu">
        {menuItems.map((item, idx) => {
          if (item.divider) {
            return (
              <div key={idx} className="sb-divider-label">
                {item.label}
              </div>
            );
          }

          const active = location.pathname === item.path;
          const hasChildren = Array.isArray(item.children);

          return (
            <li
              key={idx}
              className={active ? "active" : ""}
              onClick={() => !hasChildren && goTo(item.path)}
              onMouseEnter={() => hasChildren && setHovered(item.title)}
              onMouseLeave={() => hasChildren && setHovered(null)}
              title={item.title}
            >
              <span className="sb-icon">{item.icon}</span>
              <span className="sb-label">{item.title}</span>

              {active && <div className="sb-active-pill" />}

              {hasChildren && hovered === item.title && (
                <div className="sb-flyout">
                  {item.children.map((child, cidx) => (
                    <div
                      key={cidx}
                      className="sb-flyout-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        goTo(child.path);
                      }}
                    >
                      {child.title}
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="sb-bottom">
        {["admin", "superadmin"].includes(normalizedRole) && (
          <>
            <div className="sb-divider-label" style={{ marginTop: "10px", color: "#718096", fontSize: "10px" }}>DEVELOPER</div>
            
            <div
              className="sb-logout"
              style={{ color: "#2f855a", marginBottom: "8px", border: "1px solid #c6f6d5", background: "#f0fff4", borderRadius: "8px", opacity: 0.9 }}
              onClick={() => goTo("/salesman-ledger-manager")}
            >
              <FaBook className="sb-icon" style={{ fontSize: "14px" }} />
              <span className="sb-label" style={{ fontSize: "12px" }}>Salesman Ledger</span>
            </div>

            <div
              className="sb-logout"
              style={{ color: "#3182ce", marginBottom: "8px", border: "1px solid #bee3f8", background: "#ebf8ff", borderRadius: "8px", opacity: 0.9 }}
              onClick={() => goTo("/developer-audit")}
            >
              <FaShieldAlt className="sb-icon" style={{ fontSize: "14px" }} />
              <span className="sb-label" style={{ fontSize: "12px" }}>Developer Audit</span>
            </div>

            <div
              className="sb-logout"
              style={{ color: "#7c3aed", marginBottom: "8px", border: "1px solid #ddd6fe", background: "#f5f3ff", borderRadius: "8px", opacity: 0.9 }}
              onClick={() => goTo("/cloud-functions")}
            >
              <FaBolt className="sb-icon" style={{ fontSize: "14px" }} />
              <span className="sb-label" style={{ fontSize: "12px" }}>Cloud Functions</span>
            </div>
            
            <div
              className="sb-logout"
              style={{ color: "#d32f2f", marginBottom: "8px", border: "1px solid #ffcdd2", background: "#ffebee", borderRadius: "8px", opacity: 0.9 }}
              onClick={() => goTo("/cancel-invoices")}
            >
              <FaWindowClose className="sb-icon" style={{ fontSize: "14px" }} />
              <span className="sb-label" style={{ fontSize: "12px" }}>Cancel Invoices</span>
            </div>
          </>
        )}

        <div
          className="sb-logout"
          onClick={() =>
            signOut(auth).then(() => {
              navigate("/");
            })
          }
        >
          <FaSignOutAlt className="sb-icon" />
          <span className="sb-label">Logout</span>
        </div>

        <button
          className="sb-collapse-toggle"
          onClick={() => setCollapsed((prev) => !prev)}
        >
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
